/**
 * main.ts
 *
 * Bootstraps Vuetify and other plugins then mounts the App`
 */

// Plugins
import { registerPlugins } from '@/plugins'
import { Capacitor } from '@capacitor/core'

// Utils
import { formatDate, formatDateForPicker } from '@/utils/dateUtils'
import { getAvatarColor } from '@/utils/userUtils'
import {removeDiacritics} from '@/utils/navigateUtils'
import 'quill/dist/quill.core.css' // Required
import 'quill/dist/quill.snow.css' // For snow theme (optional)
import 'quill/dist/quill.bubble.css' // For bubble theme (optional)
import 'vue-advanced-cropper/dist/style.css'
import '@/styles/hb-table.scss'

import { setFavicons } from '@/favicons'
const brand = import.meta.env.VITE_BRAND as string
const color = import.meta.env.VITE_THEME_COLOR || '#4DBA87'
setFavicons(brand, color)

// Components
import App from './App.vue'

// Composables
import { createApp } from 'vue'

const enableNativeDiagnostics = import.meta.env.DEV || import.meta.env.MODE === 'hb-cap'

if (enableNativeDiagnostics) {
	console.log('[native-diag][boot]', {
		mode: import.meta.env.MODE,
		platform: Capacitor.getPlatform(),
		isNative: Capacitor.isNativePlatform(),
	})

	const getElementSource = (target: EventTarget | null) => {
		const element = target as
			| (HTMLElement & { src?: string; href?: string; currentSrc?: string })
			| null

		if (!element) return null

		const source = element.currentSrc || element.src || element.href || null
		const tagName = element.tagName || 'unknown-tag'

		return { tagName, source }
	}

	window.addEventListener(
		'error',
		(event) => {
			const target = event.target as EventTarget | null
			const targetInfo = getElementSource(target)

			if (target instanceof HTMLImageElement) {
				const source = target.currentSrc || target.src || 'unknown-image-source'
				console.error('[native-diag][img-error]', source)
				return
			}

			if (targetInfo?.source) {
				console.error('[native-diag][resource-error]', targetInfo.tagName, targetInfo.source)
				return
			}

			if (event.error instanceof Error) {
				const message = event.message || event.error.message || 'Unknown JS error'
				console.error('[native-diag][window-error]', message, event.error.stack)
			} else {
				const location = event.filename
					? `${event.filename}:${event.lineno}:${event.colno}`
					: 'unknown-location'
				console.error(
					'[native-diag][window-error]',
					event.message || 'Unknown JS error',
					location
				)
			}
		},
		true
	)

	window.addEventListener('unhandledrejection', (event) => {
		const reason = event.reason
		if (reason instanceof Error) {
			console.error('[native-diag][unhandledrejection]', reason.message, reason.stack)
		} else {
			console.error('[native-diag][unhandledrejection]', reason)
		}
	})

	window.addEventListener('load', () => {
		const logWebpResources = () => {
			const webpResources = performance
				.getEntriesByType('resource')
				.map((entry) => entry.name)
				.filter((name) => name.includes('.webp'))

			if (webpResources.length > 0) {
				console.log('[native-diag][webp-resources]', webpResources)
			}
		}

		window.setTimeout(logWebpResources, 2000)
		window.setTimeout(logWebpResources, 6000)
		window.setTimeout(logWebpResources, 10000)

		window.setTimeout(() => {
			const domWebpImages = Array.from(document.querySelectorAll('img'))
				.map((img) => ({
					src: img.currentSrc || img.src,
					complete: img.complete,
					naturalWidth: img.naturalWidth,
					naturalHeight: img.naturalHeight,
				}))
				.filter((entry) => entry.src.includes('.webp'))

			if (domWebpImages.length > 0) {
				console.log('[native-diag][dom-webp-state]', domWebpImages)
			}
		}, 7000)
	})
}

const app = createApp(App)

// Všechny pluginy (včetně pinia, i18n, router) se registrují v registerPlugins
registerPlugins(app)

app.config.globalProperties.$formatDate = formatDate;
app.config.globalProperties.$formatDateForPicker = formatDateForPicker;
app.config.globalProperties.$removeDiacritics = removeDiacritics;
app.config.globalProperties.$getAvatarColor = getAvatarColor;

app.mount('#app')

// Capacitor init (native only)
import { initCapacitor } from '@/capacitor'
initCapacitor()
